import { useState } from 'react';
import { FlatList, Platform, Pressable, Text, View } from 'react-native';
import { twMerge } from 'tailwind-merge';
import { BottomSheet } from './BottomSheet';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
}

interface SelectProps<T extends string = string> {
  label?: string;
  value: T | null;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string | null;
  size?: 'sm' | 'md';
  modalTitle?: string;
  className?: string;
}

export function Select<T extends string = string>({
  label,
  value,
  options,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  error,
  size = 'md',
  modalTitle,
  className,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  const sizeClasses = size === 'sm' ? 'px-3 py-2' : 'px-4 py-3';
  const textSize = size === 'sm' ? 'text-sm' : 'text-base';

  // Web: native <select> for accessibility and OS-level rendering
  if (Platform.OS === 'web') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SelectEl = 'select' as any;
    return (
      <View className={className}>
        {label && (
          <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {label}
          </Text>
        )}
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <View style={{ position: 'relative' } as any}>
          <SelectEl
            value={value ?? ''}
            disabled={disabled}
            data-sensitive="true"
            onChange={(e: { target: { value: string } }) =>
              onChange(e.target.value as T)
            }
            style={{
              width: '100%',
              padding: size === 'sm' ? '8px 32px 8px 12px' : '12px 36px 12px 16px',
              fontSize: size === 'sm' ? '14px' : '16px',
              color: value ? undefined : '#9ca3af',
              backgroundColor: 'transparent',
              border: `1px solid ${error ? '#f87171' : '#e5e7eb'}`,
              borderRadius: '12px',
              outline: 'none',
              cursor: disabled ? 'default' : 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none',
              opacity: disabled ? 0.5 : 1,
            }}
            className="text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600"
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SelectEl>
          {/* Custom caret */}
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <View
            style={{
              position: 'absolute',
              right: 12,
              top: 0,
              bottom: 0,
              justifyContent: 'center',
              pointerEvents: 'none',
            } as any}
          >
            <Text className="text-xs text-gray-400 dark:text-gray-500">▼</Text>
          </View>
        </View>
        {error && (
          <Text className="text-xs text-red-600 dark:text-red-400 mt-1">
            {error}
          </Text>
        )}
      </View>
    );
  }

  // Native: bottom sheet picker
  return (
    <View className={className}>
      {label && (
        <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          {label}
        </Text>
      )}
      <Pressable
        onPress={() => !disabled && setOpen(true)}
        className={twMerge(
          'flex-row items-center justify-between rounded-xl bg-white dark:bg-gray-800 border',
          error
            ? 'border-red-400 dark:border-red-500'
            : 'border-gray-200 dark:border-gray-600',
          sizeClasses,
          disabled && 'opacity-50',
        )}
      >
        <Text
          className={twMerge(
            'flex-1',
            textSize,
            selected
              ? 'text-gray-800 dark:text-gray-100'
              : 'text-gray-400 dark:text-gray-500',
          )}
        >
          {selected?.label ?? placeholder}
        </Text>
        <Text className="text-xs font-semibold text-gray-400 dark:text-gray-500 ml-2 mr-1">
          ▼
        </Text>
      </Pressable>
      {error && (
        <Text className="text-xs text-red-600 dark:text-red-400 mt-1">
          {error}
        </Text>
      )}

      <BottomSheet
        visible={open}
        onClose={() => setOpen(false)}
        title={modalTitle ?? label ?? 'Select'}
      >
        <FlatList
          data={options}
          keyExtractor={(item) => item.value}
          renderItem={({ item }) => {
            const isSelected = item.value === value;
            return (
              <Pressable
                onPress={() => {
                  onChange(item.value);
                  setOpen(false);
                }}
                className={twMerge(
                  'flex-row items-center justify-between px-5 py-3.5 border-b border-gray-50 dark:border-gray-700',
                  isSelected && 'bg-green-50 dark:bg-green-900/30',
                )}
              >
                <Text
                  className={twMerge(
                    'text-[15px]',
                    isSelected
                      ? 'text-green-700 dark:text-green-400 font-semibold'
                      : 'text-gray-800 dark:text-gray-200',
                  )}
                >
                  {item.label}
                </Text>
              </Pressable>
            );
          }}
        />
      </BottomSheet>
    </View>
  );
}
